/**
 * Клиент телеграма — один на всё приложение.
 *
 * Все обращения к `window.Telegram` собраны здесь, и ни один экран
 * не знает, что он открыт в телеграме. Причины две.
 *
 * Первая: приложение обязано работать и без телеграма — в браузере
 * на полигоне, где `window.Telegram` просто нет. Одна проверка в одном
 * месте вместо пятнадцати по экранам.
 *
 * Вторая: телеграм меняет свой интерфейс версиями, и методы, которых нет
 * в старом клиенте, бросают исключение при вызове. Обёртки ниже глотают
 * это молча: не показать отклик вибрацией — не повод уронить оплату.
 */

interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

interface MainButton {
  setText: (text: string) => MainButton;
  show: () => MainButton;
  hide: () => MainButton;
  enable: () => MainButton;
  disable: () => MainButton;
  showProgress: (leaveActive?: boolean) => MainButton;
  hideProgress: () => MainButton;
  onClick: (handler: () => void) => MainButton;
  offClick: (handler: () => void) => MainButton;
  setParams: (params: { color?: string; text_color?: string }) => MainButton;
}

interface BackButton {
  show: () => BackButton;
  hide: () => BackButton;
  onClick: (handler: () => void) => BackButton;
  offClick: (handler: () => void) => BackButton;
}

interface Haptic {
  impactOccurred: (style: 'light' | 'medium' | 'heavy') => void;
  notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
  selectionChanged: () => void;
}

export interface WebApp {
  initData: string;
  version: string;
  colorScheme: 'light' | 'dark';
  themeParams: ThemeParams;
  viewportHeight: number;
  viewportStableHeight: number;
  isExpanded: boolean;
  MainButton: MainButton;
  BackButton: BackButton;
  HapticFeedback: Haptic;
  ready: () => void;
  expand: () => void;
  close: () => void;
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink: (url: string) => void;
  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;
  onEvent: (event: string, handler: () => void) => void;
  offEvent: (event: string, handler: () => void) => void;
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: WebApp };
  }
}

/** Клиент или ничего. Ничего — это браузер на полигоне, и это нормально. */
export function tg(): WebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/** Мы действительно внутри телеграма, а не просто открыли страницу. */
export function isInsideTelegram(): boolean {
  const app = tg();
  return Boolean(app && app.initData.length > 0);
}

/**
 * Подпись телеграма для запросов к серверу.
 *
 * Уходит заголовком, а не в теле: тело у разных ручек разное, а проверка
 * одна, и в заголовке её не забудут при следующей правке схемы.
 */
export function initDataHeader(): Record<string, string> {
  const app = tg();
  return app?.initData ? { 'X-Telegram-Init-Data': app.initData } : {};
}

/** Значение `startapp` из ссылки: ключ заказа или черновика. */
export function startParam(): string | null {
  const app = tg();
  if (!app) return null;

  // Телеграм кладёт его в initData как `start_param`. Разбираем строку
  // сами: разбирать её всё равно придётся серверу, а нам нужен один ключ.
  const value = new URLSearchParams(app.initData).get('start_param');
  return value && value.length > 0 ? value : null;
}

/** Действия, которых может не быть в старом клиенте. */
function quiet(action: () => void): void {
  try {
    action();
  } catch {
    // Метода нет в этой версии клиента. Приложение работает дальше.
  }
}

export function ready(): void {
  const app = tg();
  if (!app) return;

  quiet(() => {
    app.ready();
    // Разворачиваем сразу: приложение открывается в половину экрана,
    // и человек первым делом тянет его вверх. Незачем заставлять.
    app.expand();
  });
}

export function openExternal(url: string): void {
  const app = tg();

  if (app) {
    // Внутренним браузером клиента: так человек остаётся в телеграме
    // и возвращается в приложение одним нажатием, а не через список задач.
    quiet(() => app.openLink(url));
    return;
  }

  window.open(url, '_blank', 'noopener');
}

export function haptic(kind: 'select' | 'ok' | 'error'): void {
  const app = tg();
  if (!app) return;

  quiet(() => {
    if (kind === 'select') app.HapticFeedback.selectionChanged();
    if (kind === 'ok') app.HapticFeedback.notificationOccurred('success');
    if (kind === 'error') app.HapticFeedback.notificationOccurred('error');
  });
}

/**
 * Подтверждение закрытия.
 *
 * Включается, как только человеку есть что терять: свайп вниз в телеграме
 * закрывает приложение мгновенно и без вопросов, а вместе с ним — полчаса
 * работы над принтом.
 */
export function guardClosing(isDirty: boolean): void {
  const app = tg();
  if (!app) return;

  quiet(() => (isDirty ? app.enableClosingConfirmation() : app.disableClosingConfirmation()));
}
